import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import {
  FiPlus,
  FiTrash2,
  FiSearch,
  FiInbox,
  FiLayers,
  FiList,
  FiSettings,
} from "react-icons/fi";
import {
  StandardInfoAtom,
  ListStandardInfoAtom,
  DetailStandardInfoAtom,
} from "../../../atoms/standardModelAtom";
import {
  tokens,
  PageShell,
  PageWidth,
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  IconBtn,
  Input,
  Label,
  Field,
  SearchInput,
  TableCard,
  TableScroll,
  Table,
  Th,
  Td,
  Tr,
  Tag,
  SideList,
  SideItem,
  EmptyState,
} from "../../../components/admin/AdminUI";

const Grid = styled.div`
  display: grid;
  grid-template-columns: 300px 360px minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 1200px) {
    grid-template-columns: 280px minmax(0, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* 패널: 동일 높이로 늘어나고 내부에서만 스크롤 */
const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  @media (max-width: 1200px) {
    min-height: 420px;
  }
`;

const PanelBody = styled(CardBody)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const AddBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${tokens.color.borderSubtle};
`;

const CodeKey = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${tokens.color.heading};
`;

const CodeMeta = styled.div`
  font-size: 13px;
  color: ${tokens.color.muted};
  margin-top: 2px;
`;

const CodeDesc = styled.div`
  font-size: 12px;
  color: ${tokens.color.subtle};
  margin-top: 2px;
`;

const CodeItemBtn = styled(SideItem)`
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  border: 1px solid
    ${(p) => (p.$active ? tokens.color.primaryRing : tokens.color.borderSubtle)};
  margin-bottom: 6px;
`;

export default function StandardInfo_Dtl() {
  const [codeGroupInput, setCodeGroupInput] = useRecoilState(StandardInfoAtom);
  const [selectedCodeGroup, setSelectedCodeGroup] = useRecoilState(ListStandardInfoAtom);
  const [selectedCodeGroupDetails, setSelectedCodeGroupDetails] = useRecoilState(DetailStandardInfoAtom);

  const [filterValue, setFilterValue] = useState("");
  const [codeGroupList, setCodeGroupList] = useState([]);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [codeOptionList, setCodeOptionList] = useState([]);

  // Form states for adding new options
  const [optionKey, setOptionKey] = useState("");
  const [optionValue, setOptionValue] = useState("");
  const [optionDesc, setOptionDesc] = useState("");

  useEffect(() => {
    getItemSample();
  }, []);

  const getItemSample = async () => {
    try {
      const res = await fetch("/request/getCommonCodeGroup", {
        credentials: "include",
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setCodeGroupList(data);
      }
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const getStdDtl = async (codeGroup) => {
    try {
      const res = await fetch(`/request/getStdDtl?std_group_code=${codeGroup}`, {
        credentials: "include"
      });
      const data = await res.json();
      setSelectedCodeGroupDetails(data);
      setSelectedDetail(null);
      setCodeOptionList([]);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const getStdOptions = async (codeGroup, std_value) => {
    try {
      const res = await fetch(
        `/request/getStdOptions?std_group_code=${codeGroup}&std_value=${std_value}`,
        { credentials: "include" }
      );
      const data = await res.json();
      setCodeOptionList(data);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const insertStdInfo = async (codeGroupInput) => {
    if (!codeGroupInput.trim()) return;
    try {
      const res = await fetch("/request/InsertStdInfo", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codeGroupInput }),
      });
      await res.json();
      alert(`새 코드그룹 [${codeGroupInput}] 생성`);
      setCodeGroupInput("");
      getItemSample();
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const deleteStdInfo = async (codeGroup) => {
    if (!codeGroup) {
      alert("삭제할 코드그룹을 선택해주세요.");
      return;
    }
    if (!window.confirm(`${codeGroup}을 정말 삭제하시겠습니까?`)) return;

    try {
      const res = await fetch("/request/deleteStdInfo", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_code: codeGroup }),
      });
      if (!res.ok) throw new Error(`Server responded with status code ${res.status}`);

      alert("삭제 되었습니다.");
      setSelectedCodeGroupDetails([]);
      setSelectedDetail(null);
      setSelectedCodeGroup("");
      getItemSample();
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const handleCodeGroupClick = (codeGroup) => {
    getStdDtl(codeGroup);
    setSelectedCodeGroup(codeGroup);
  };

  const handleDetailClick = (detail) => {
    setSelectedDetail(detail);
    getStdOptions(detail.std_group_code, detail.std_value);
  };

  const getFilteredRows = () => {
    const filterText = filterValue.toLowerCase();
    return codeGroupList.filter((row) =>
      row.std_group_code.toLowerCase().includes(filterText)
    );
  };

  const handleInsertNewOption = async () => {
    if (!selectedDetail) {
      alert("먼저 코드를 선택해주세요.");
      return;
    }

    if (!optionKey.trim() || !optionValue.trim()) {
      alert("옵션과 값을 입력해주세요.");
      return;
    }

    try {
      const res = await fetch("/request/InsertStdInfoDtlOption", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_code: selectedCodeGroup,
          optionName: optionKey,
          optionValue: optionValue,
          optionDesc: optionDesc,
          target_code: selectedDetail.std_value,
          key: selectedDetail.std_key,
          value: selectedDetail.std_value,
          description: selectedDetail.std_description,
        }),
      });
      await res.json();
      alert(`코드그룹 [${selectedCodeGroup}]에 새로운 옵션이 추가되었습니다.`);

      // Clear form
      setOptionKey("");
      setOptionValue("");
      setOptionDesc("");

      // Refresh options
      getStdOptions(selectedCodeGroup, selectedDetail.std_value);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const handleDeleteOption = async (optionId, optionName) => {
    if (!window.confirm(`옵션 "${optionName}"을 정말 삭제하시겠습니까?`)) return;

    try {
      const res = await fetch("/request/deleteStdInfoDtlOption", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          option_id: optionId,
          group_code: selectedCodeGroup,
          target_code: selectedDetail.std_value
        }),
      });

      if (!res.ok) throw new Error(`Failed to delete option: ${optionId}`);

      alert("옵션이 삭제되었습니다.");
      // Refresh options
      getStdOptions(selectedCodeGroup, selectedDetail.std_value);
    } catch (error) {
      console.log("Error: " + error);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  const filteredRows = getFilteredRows();

  return (
    <PageShell>
      <PageWidth>
        <PageHeader
          eyebrow="MASTER DATA"
          title="기준정보 상세"
          subtitle="코드그룹과 코드, 옵션 정보를 조회하고 관리합니다."
        />

        <Grid>
          {/* Left Panel - Group List */}
          <Panel $pad={false}>
            <CardHeader>
              <CardTitle>
                <FiLayers /> 그룹 리스트
              </CardTitle>
            </CardHeader>
            <PanelBody>
              <SearchInput
                placeholder="찾을 코드를 입력해주세요."
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                grow="0 0 auto"
              />
              <ScrollArea style={{ marginTop: 14 }}>
                {filteredRows.length === 0 ? (
                  <EmptyState
                    icon={<FiSearch />}
                    title="코드그룹이 없습니다"
                    description="검색 조건과 일치하는 코드그룹이 없습니다."
                    pad="32px 16px"
                  />
                ) : (
                  <SideList>
                    {filteredRows.map((row, index) => (
                      <SideItem
                        key={index}
                        $active={selectedCodeGroup === row.std_group_code}
                        onClick={() => handleCodeGroupClick(row.std_group_code)}
                      >
                        {row.std_group_code}
                      </SideItem>
                    ))}
                  </SideList>
                )}
              </ScrollArea>

              <AddBar>
                <Input
                  placeholder="새 코드그룹명을 입력하세요."
                  value={codeGroupInput}
                  onChange={(e) => setCodeGroupInput(e.target.value)}
                />
                <IconBtn
                  title="선택 그룹 삭제"
                  onClick={() => deleteStdInfo(selectedCodeGroup)}
                >
                  <FiTrash2 />
                </IconBtn>
                <Button
                  $variant="primary"
                  onClick={() => insertStdInfo(codeGroupInput)}
                >
                  <FiPlus /> 추가
                </Button>
              </AddBar>
            </PanelBody>
          </Panel>

          {/* Middle Panel - Code List */}
          <Panel $pad={false}>
            <CardHeader>
              <CardTitle>
                <FiList /> 코드 리스트
              </CardTitle>
            </CardHeader>
            <PanelBody>
              <ScrollArea>
                {selectedCodeGroupDetails.length === 0 ? (
                  <EmptyState
                    icon={<FiInbox />}
                    title="코드가 없습니다"
                    description="좌측에서 코드그룹을 선택하면 코드 목록이 표시됩니다."
                    pad="32px 16px"
                  />
                ) : (
                  selectedCodeGroupDetails.map((detail, index) => (
                    <CodeItemBtn
                      key={index}
                      $active={selectedDetail && selectedDetail.std_key === detail.std_key}
                      onClick={() => handleDetailClick(detail)}
                    >
                      <CodeKey>{detail.std_key}</CodeKey>
                      <CodeMeta>{detail.std_value}</CodeMeta>
                      <CodeDesc>{detail.std_description}</CodeDesc>
                    </CodeItemBtn>
                  ))
                )}
              </ScrollArea>
            </PanelBody>
          </Panel>

          {/* Right Panel - Option Management */}
          <Panel $pad={false}>
            <CardHeader>
              <CardTitle>
                <FiSettings /> 옵션 정보 관리
              </CardTitle>
            </CardHeader>
            <PanelBody>
              {selectedDetail && (
                <Card $pad="18px" style={{ marginBottom: 20 }}>
                  <CardTitle style={{ marginBottom: 16 }}>
                    선택된 코드: {selectedDetail.std_key} ({selectedDetail.std_value})
                  </CardTitle>

                  <Field>
                    <Label>옵션명</Label>
                    <Input
                      type="text"
                      placeholder="옵션명을 입력하세요"
                      value={optionKey}
                      onChange={(e) => setOptionKey(e.target.value)}
                    />
                  </Field>

                  <Field>
                    <Label>옵션값</Label>
                    <Input
                      type="text"
                      placeholder="옵션값을 입력하세요"
                      value={optionValue}
                      onChange={(e) => setOptionValue(e.target.value)}
                    />
                  </Field>

                  <Field $mb="0">
                    <Label>설명</Label>
                    <Input
                      type="text"
                      placeholder="설명을 입력하세요"
                      value={optionDesc}
                      onChange={(e) => setOptionDesc(e.target.value)}
                    />
                  </Field>

                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                    <Button $variant="primary" onClick={handleInsertNewOption}>
                      <FiPlus /> 옵션 추가
                    </Button>
                  </div>
                </Card>
              )}

              <Label style={{ marginBottom: 10 }}>등록된 옵션 정보</Label>
              <TableCard>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <Th>코드그룹</Th>
                        <Th>대상코드</Th>
                        <Th>옵션명</Th>
                        <Th>옵션값</Th>
                        <Th>설명</Th>
                        <Th $align="right">삭제</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {codeOptionList.length === 0 ? (
                        <tr>
                          <td colSpan={6}>
                            <EmptyState
                              icon={<FiInbox />}
                              title="등록된 옵션이 없습니다"
                              description="코드를 선택하고 옵션을 추가해 보세요."
                            />
                          </td>
                        </tr>
                      ) : (
                        codeOptionList.map((option, index) => (
                          <Tr key={index}>
                            <Td>
                              <Tag>{option.code_group}</Tag>
                            </Td>
                            <Td $muted>{option.target_code}</Td>
                            <Td style={{ fontWeight: 600, color: tokens.color.heading }}>
                              {option.code_option}
                            </Td>
                            <Td>{option.code_value}</Td>
                            <Td $muted>{option.code_desc}</Td>
                            <Td $align="right">
                              <Button
                                $variant="dangerSoft"
                                $size="sm"
                                onClick={() => handleDeleteOption(option.id || option.option_id, option.code_option)}
                              >
                                <FiTrash2 /> 삭제
                              </Button>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </TableScroll>
              </TableCard>
            </PanelBody>
          </Panel>
        </Grid>
      </PageWidth>
    </PageShell>
  );
}
